"use strict";
/*
 * all of these assertions take either a string, throw function, or nothing
 * for the second argument. its STRONGLY encouraged to provide some error
 * or message that explains the reason for the assertion
 *
 * eg:
 *   assertDefined(user, new UnauthorizedException())
 *   assertDefined(thing.otherThing, 'otherThing should be guaranteed to exist, but is missing')
 *
 * passing an Error instead of a string makes the first element of the resulting
 * stack trace more useful, and allows you to use specific error types
 * that might be handled differently.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.assertErrorInstanceOf = exports.assertInstanceOf = exports.notNaN = exports.assertNotNaN = exports.assertString = exports.assertDefined = exports.assertFalse = exports.assertTrue = exports.doThrow = void 0;
const doThrow = (failed) => {
    if (typeof failed === 'string') {
        throw new Error(failed);
    }
    if (failed instanceof Error) {
        throw failed;
    }
    if (!failed) {
        throw new Error();
    }
    return failed();
};
exports.doThrow = doThrow;
/**
 * Asserts that the given value is true.
 *
 * @param x The value to assert.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelyTrue = assertTrue(randomThing, new Error('thing was not true'));
 * @returns the value that was asserted
 */
const assertTrue = (x, failed) => {
    if (typeof x !== 'boolean' || x !== true) {
        return (0, exports.doThrow)(failed);
    }
    return x;
};
exports.assertTrue = assertTrue;
/**
 * Asserts that the given value is false.
 *
 * @param x The value to assert.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelyFalse = assertFalse(randomThing, new Error('thing was not false'));
 * @returns the value that was asserted
 */
const assertFalse = (x, failed) => {
    if (typeof x !== 'boolean' || x !== false) {
        return (0, exports.doThrow)(failed);
    }
    return x;
};
exports.assertFalse = assertFalse;
/**
 * Asserts that the given value is defined.
 *
 * @param x The value to assert.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelyDefined = assertDefined(randomThing, new Error('thing was undefined'));
 * @returns the value that was asserted, with a type that excludes undefined
 */
const assertDefined = (x, failed) => {
    if (x === undefined) {
        return (0, exports.doThrow)(failed);
    }
    return x;
};
exports.assertDefined = assertDefined;
/**
 * Asserts that the given value is a string.
 *
 * @param x The value to assert.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelyAString = assertString(randomThing, new Error('thing is not a string'));
 * @returns the value that was asserted
 */
const assertString = (x, failed) => {
    if (typeof x !== 'string') {
        return (0, exports.doThrow)(failed);
    }
    return x;
};
exports.assertString = assertString;
/**
 * Asserts that the given value is not `NaN`. Does not assert that the value is a number.
 *
 * @param thing The value to assert.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelyNotNotANumber = assertNotNaN(randomThing, new Error('thing was NaN'));
 * @returns the value that was asserted
 */
const assertNotNaN = (thing, failed) => {
    if (typeof thing === 'number' && isNaN(thing)) {
        return (0, exports.doThrow)(failed);
    }
    return thing;
};
exports.assertNotNaN = assertNotNaN;
/**
 * @deprecated use assertNotNaN instead
 */
exports.notNaN = exports.assertNotNaN;
/**
 * Asserts that the first argument is an instance of the second.
 *
 * @param thing The value to assert.
 * @param constructable The class to check against.
 * @param failed The error to throw if the assertion fails. It is STRONGLY encouraged
 *  to provide some value for this for traceability. Passing an Error instead of a string
 *  makes the stack trace more useful and allows you to handle assertion failures differently.
 * @example const definitelySyntaxError = assertInstanceOf(error, SyntaxError, new Error('argument was not a SyntaxError'));
 * @returns the value that was asserted
 */
const assertInstanceOf = (thing, constructable, failed) => {
    if (thing instanceof constructable) {
        return thing;
    }
    return (0, exports.doThrow)(failed);
};
exports.assertInstanceOf = assertInstanceOf;
/**
 * Asserts that the error in the first argument is an instance of the error given as the
 * second argument
 *
 * @param thing The value to assert.
 * @param constructable The error class to check against.
 * @example const definitelySyntaxError = assertInstanceOf(error, SyntaxError);
 * @returns the value that was asserted
 * @throws the original error if the check fails
 * @see assertInstanceOf
 */
const assertErrorInstanceOf = (thing, constructable) => {
    if (thing instanceof Error) {
        return (0, exports.assertInstanceOf)(thing, constructable, thing);
    }
    else {
        // this separate branch prevents an unknown non-Error thing from being passed to the assertion as an AssertionFailed
        throw new Error(`assertErrorInstanceOf received non-Error argument of type "${typeof thing}" and string representation "${thing}"`);
    }
};
exports.assertErrorInstanceOf = assertErrorInstanceOf;
